// ListPromo.tsx
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ArrowDown, ArrowUp, Check, ChevronDown, CirclePlus, PenIcon, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent } from '@radix-ui/react-dropdown-menu';
import { Button } from '../../components/ui/button';
import { DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import Input from '../../components/ui/input';
import usePromotion from '../../stores/usePromo';
import { IPromotion } from '../../types/types';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import Skeleton from '../../components/ui/skeleton';
import InfiniteScrollObserver from '../../components/InfiniteScrollObserver';

function formatPrice(price: number): string {
  if (price >= 1_000_000) {
    return `${(price / 1_000_000).toFixed(2).replace('.00', '')}JT`;
  }
  if (price >= 100_000) {
    return `${Math.round(price / 1_000)}RB`;
  }
  return price.toLocaleString('id-ID');
}

interface PromoCardProps {
  id: number;
  name?: string;
  service?: string;
  price?: number;
  imageUrl?: string;
  onEdit: () => void;
  onDelete: () => void;
  isSelected?: boolean;
  onSelect?: () => void;
  isSelectMode?: boolean;
}

function PromoCard({
  id,
  name,
  service,
  price,
  imageUrl,
  onEdit,
  onDelete,
  isSelectMode,
  isSelected,
  onSelect,
}: PromoCardProps) {
  return (
    <button
      type="button"
      className={`sm:h-64 h-48 border border-border overflow-hidden bg-cover rounded-lg cursor-pointer hover:drop-shadow-md bg-center transition-all duration-300 ease-in-out ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      }`}
      onClick={isSelectMode ? onSelect : undefined}
      style={{ backgroundImage: `url(${imageUrl || '/assets/no-data-image.svg'})` }}
    >
      <div className="bg-gradient-to-t from-primary to-transparent h-full w-full text-white flex justify-between items-center flex-col hover:bg-black/50 transition-all duration-300 ease-in-out">
        <div className="flex w-full justify-end sm:p-4 p-2">
          {!isSelectMode ? (
            <div className="flex gap-2 px-2 bg-neutral-50/80 w-fit py-1.5 items-center border border-border rounded-lg">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                <Trash2 className="h-4 text-red-600" />
                <span className="sr-only">Delete</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <PenIcon className="h-4 text-primary" />
                <span className="sr-only">Edit</span>
              </button>
            </div>
          ) : (
            isSelected && (
              <div className="bg-blue-500 rounded-full p-1">
                <Check className="h-4 w-4 text-white" />
              </div>
            )
          )}
        </div>
        <div className="flex flex-col drop-shadow-lg items-center gap-4 px-2 py-2.5 sm:px-4 sm:py-5">
          <div className="flex flex-col items-center">
            <p className="sm:text-sm text-xs font-medium">{service}</p>
            <h1 className="font-semibold text-center text-xl sm:text-2xl capitalize">{name}</h1>
          </div>
          <div className="flex sm:gap-2 gap-1.5">
            <div className="flex flex-col items-end justify-start w-fit">
              <p className="sm:text-[0.5rem] text-[6px] font-medium">Mulai dari</p>
              <p className="sm:text-sm text-xs font-semibold">IDR</p>
            </div>
            <div className="sm:text-4xl text-2xl text-currency font-semibold">
              <h1>{formatPrice(price || 0)}</h1>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

interface SelectedPromo {
  id: number;
  name: string;
}

export default function ListPromo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const {
    promotions,
    fetchLoading,
    removeLoading,
    fetchPromotions,
    removePromotion,
    page,
    hasMore,
    hasFetched,
    isLoadingNextPage,
  } = usePromotion();
  const [selectedPromo, setSelectedPromo] = useState<SelectedPromo | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [sortBy, setSortBy] = useState<'name_prm' | 'created_at' | 'edited_at'>(() => {
    const savedSortBy = localStorage.getItem('promoSortBy');
    return savedSortBy ? (savedSortBy as 'name_prm' | 'created_at' | 'edited_at') : 'edited_at';
  });
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(() => {
    const savedSortOrder = localStorage.getItem('promoSortOrder');
    return savedSortOrder ? (savedSortOrder as 'desc' | 'asc') : 'desc';
  });

  useEffect(() => {
    if (!hasFetched) {
      fetchPromotions(1, true);
    }
  }, [fetchPromotions, hasFetched]);

  const handleLoadMore = () => {
    fetchPromotions(page + 1, false);
  };

  const handleTabClick = () => {
    setSearchParams({ tab: 'form' });
  };

  const handleEdit = (id: number) => {
    setSearchParams({ tab: 'form', edit: String(id) });
  };

  const handleDeleteClick = (id: number, name: string) => {
    setSelectedPromo({ id, name });
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedPromo) {
      await removePromotion(selectedPromo.id);
      setIsModalOpen(false);
      setSelectedPromo(null);
    } else if (selectedIds.length > 0) {
      const deleteWithDelay = async (ids: number[], index = 0) => {
        if (index >= ids.length) {
          setSelectedIds([]);
          setIsModalOpen(false);
          setIsSelectMode(false);
          return;
        }

        await removePromotion(ids[index]);
        setTimeout(() => deleteWithDelay(ids, index + 1), 200);
      };

      await deleteWithDelay(selectedIds);
    }
  };

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    setSelectedIds([]);
  };

  const handleSelect = (id: number) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]));
  };

  const handleDeleteSelected = () => {
    if (selectedIds.length > 0) {
      setIsModalOpen(true);
    }
  };

  const handleSortChange = (field: 'created_at' | 'name_prm' | 'edited_at') => {
    setSortBy(field);
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === 'desc' ? 'asc' : 'desc'));
  };

  const filteredPromo = promotions
    .filter((promo: IPromotion) => {
      const nameMatches = promo.name_prm.toLowerCase().includes(searchTerm.toLowerCase());
      const serviceMatches =
        promo.service && promo.service.name_srv_id
          ? promo.service.name_srv_id.toLowerCase().includes(searchTerm.toLowerCase())
          : false;
      return nameMatches || serviceMatches;
    })
    .sort((a: IPromotion, b: IPromotion) => {
      const valueA = a[sortBy];
      const valueB = b[sortBy];

      if (sortBy === 'name_prm') {
        return sortOrder === 'asc'
          ? String(valueA).localeCompare(String(valueB))
          : String(valueB).localeCompare(String(valueA));
      }

      if (sortBy === 'created_at' || sortBy === 'edited_at') {
        return sortOrder === 'asc'
          ? new Date(valueA || '').getTime() - new Date(valueB || '').getTime()
          : new Date(valueB || '').getTime() - new Date(valueA || '').getTime();
      }

      return 0;
    });

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-2">
        <div className="flex justify-between items-center w-full">
          <h1 className="font-medium text-md">Manage Promo</h1>
          <div className="flex gap-2">
            {isSelectMode && (
              <Button variant="destructive" onClick={handleDeleteSelected} disabled={selectedIds.length === 0}>
                <Trash2 size={16} />
                <span className="hidden md:inline ml-1">Delete Selected ({selectedIds.length})</span>
              </Button>
            )}
            <Button variant="outline" onClick={toggleSelectMode}>
              {isSelectMode ? 'Cancel' : 'Select'}
            </Button>
            {!isSelectMode && (
              <Button variant="default" onClick={handleTabClick}>
                <CirclePlus size={16} />
                <span className="hidden md:inline">Create New Product</span>
              </Button>
            )}
          </div>
        </div>
        <div className="flex justify-between items-center w-full md:flex-row flex-col">
          <Input
            placeholder="Search by name or service"
            className="lg:max-w-sm w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex justify-between md:w-fit w-full">
            <Button
              variant="ghost"
              type="button"
              className="p-2 hover:bg-neutral-50 rounded-lg"
              onClick={toggleSortOrder}
            >
              <span className="sr-only">Sort Order</span>
              {sortOrder === 'asc' ? <ArrowUp className="h-4" /> : <ArrowDown className="h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-2 hover:bg-neutral-50 text-sm rounded-lg flex gap-1">
                  {sortBy === 'name_prm' && 'Nama'}
                  {sortBy === 'created_at' && 'Dibuat'}
                  {sortBy === 'edited_at' && 'Terakhir diubah'}
                  <ChevronDown className="h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-border bg-white shadow border p-1 z-50 rounded-lg">
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('name_prm')}>
                    Nama
                  </button>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('created_at')}>
                    Dibuat
                  </button>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <button type="button" onClick={() => handleSortChange('edited_at')}>
                    Terakhir diubah
                  </button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="grid sm:grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2 w-full">
        {fetchLoading ? (
          <section className="grid sm:grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2 w-full col-span-full">
            <Skeleton className="sm:h-64 h-48" />
            <Skeleton className="sm:h-64 h-48" />
            <Skeleton className="sm:h-64 h-48" />
            <Skeleton className="sm:h-64 h-48" />
            <Skeleton className="sm:h-64 h-48" />
          </section>
        ) : (
          <section className="grid sm:grid-cols-[repeat(auto-fill,_minmax(12rem,_1fr))] grid-cols-[repeat(auto-fill,_minmax(8rem,_1fr))] gap-2 w-full col-span-full">
            {filteredPromo.length > 0 ? (
              filteredPromo.map((promo: IPromotion) => (
                <PromoCard
                  key={promo.promo_id}
                  id={promo.promo_id}
                  name={promo.name_prm}
                  price={promo.price_prm}
                  service={promo.service?.name_srv_id}
                  imageUrl={promo.image_prm || '/assets/no-data-image.svg'}
                  onEdit={() => handleEdit(promo.promo_id)}
                  onDelete={() => handleDeleteClick(promo.promo_id, promo.name_prm)}
                  isSelectMode={isSelectMode}
                  isSelected={selectedIds.includes(promo.promo_id)}
                  onSelect={() => handleSelect(promo.promo_id)}
                />
              ))
            ) : (
              <p className="px-3 py-2 col-span-full text-center text-neutral-300">No data</p>
            )}
            {isLoadingNextPage && <Skeleton className="sm:h-64 h-48" />}
          </section>
        )}

        <InfiniteScrollObserver
          hasMore={hasMore}
          isLoading={fetchLoading || isLoadingNextPage}
          onLoadMore={handleLoadMore}
        />
      </main>

      <ConfirmDeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={selectedPromo ? selectedPromo?.name : `${selectedIds.length} selected items`}
        loading={removeLoading}
      />
    </div>
  );
}
